import { useCallback, useEffect, useRef, useState } from 'react';
import type { Editor } from '@tiptap/react';
// นำเข้าเพื่อให้ TypeScript รู้จักคำสั่งตาราง (insertTable, addRowAfter, ...) ที่ extension ประกาศเพิ่มไว้
import '@tiptap/extension-table';
import { CELL_VERTICAL_ALIGNS, type CellVerticalAlign } from '@blogaris/shared';
import { cx, IconTrash } from '../../components/ui/index.js';

/* ------------------------------------------------------------------ *
 * ตัวเลือกขนาดตารางแบบกวาดเมาส์ (แบบเดียวกับ Word)
 * ------------------------------------------------------------------ */
const MAX_ROWS = 8;
const MAX_COLS = 8;

export function TableGridPicker({
  editor, disabled,
}: { editor: Editor; disabled?: boolean }) {
  const [open, setOpen] = useState(false);
  const [hover, setHover] = useState<{ r: number; c: number } | null>(null);
  const boxRef = useRef<HTMLDivElement | null>(null);

  // คลิกที่อื่นแล้วปิด — ต้องฟังตอน mousedown ไม่ใช่ click ไม่งั้นกดปุ่มในเอดิเตอร์แล้วเมนูค้าง
  useEffect(() => {
    if (!open) return;
    const onDown = (e: MouseEvent) => {
      if (!boxRef.current?.contains(e.target as Node)) setOpen(false);
    };
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') setOpen(false); };
    document.addEventListener('mousedown', onDown);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDown);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  const insert = useCallback((rows: number, cols: number) => {
    setOpen(false);
    setHover(null);
    // แถวแรกเป็นหัวตารางเสมอ — ตารางในบทความเกือบทั้งหมดต้องการหัวคอลัมน์
    editor.chain().focus().insertTable({ rows, cols, withHeaderRow: true }).run();
  }, [editor]);

  return (
    <div ref={boxRef} className="relative">
      <button
        type="button"
        title="แทรกตาราง"
        aria-label="แทรกตาราง"
        aria-expanded={open}
        disabled={disabled}
        onClick={() => setOpen((v) => !v)}
        className={cx(
          'grid h-8 min-w-8 place-items-center rounded-md px-2 text-sm transition-colors',
          open
            ? 'bg-[var(--brand)] text-[var(--brand-ink)]'
            : 'text-[var(--ink-2)] hover:bg-[var(--sunken)] hover:text-[var(--ink)]',
          disabled && 'cursor-not-allowed opacity-40',
        )}
      >
        <svg width="15" height="15" viewBox="0 0 16 16" aria-hidden="true">
          <rect x="1" y="2.5" width="14" height="11" rx="1.5" fill="none" stroke="currentColor" strokeWidth="1.4" />
          <path d="M1 6.2h14M1 10h14M5.9 2.5v11M10.4 2.5v11" stroke="currentColor" strokeWidth="1.1" />
        </svg>
      </button>

      {open && (
        <div
          className="absolute left-0 top-full z-30 mt-1 rounded-xl border border-[var(--line)] bg-[var(--surface)] p-2.5 shadow-lg"
          onMouseLeave={() => setHover(null)}
        >
          <div
            className="grid gap-[3px]"
            style={{ gridTemplateColumns: `repeat(${MAX_COLS}, 18px)` }}
          >
            {Array.from({ length: MAX_ROWS * MAX_COLS }, (_, i) => {
              const r = Math.floor(i / MAX_COLS) + 1;
              const c = (i % MAX_COLS) + 1;
              const on = hover !== null && r <= hover.r && c <= hover.c;
              return (
                <button
                  key={i}
                  type="button"
                  aria-label={`${r} แถว ${c} คอลัมน์`}
                  onMouseEnter={() => setHover({ r, c })}
                  onClick={() => insert(r, c)}
                  className={cx(
                    'h-[18px] rounded-[3px] border transition-colors',
                    on
                      ? 'border-[var(--brand)] bg-[var(--brand)]'
                      : 'border-[var(--line)] bg-[var(--sunken)]',
                  )}
                />
              );
            })}
          </div>
          <p className="mt-2 text-center text-xs tabular-nums text-[var(--ink-2)]">
            {hover ? `${hover.r} × ${hover.c}` : 'กวาดเลือกขนาด'}
          </p>
        </div>
      )}
    </div>
  );
}

/* ------------------------------------------------------------------ *
 * แถบจัดการตาราง — โผล่เฉพาะตอนเคอร์เซอร์อยู่ในตาราง
 * ------------------------------------------------------------------ */
function ToolButton({
  onClick, title, disabled, danger, active, children,
}: {
  onClick: () => void; title: string; disabled?: boolean; danger?: boolean;
  active?: boolean; children: React.ReactNode;
}) {
  return (
    <button
      type="button"
      title={title}
      disabled={disabled}
      onMouseDown={(e) => e.preventDefault()}
      onClick={onClick}
      aria-pressed={active}
      className={cx(
        'flex h-7 items-center gap-1 rounded-md px-2 text-xs font-medium transition-colors',
        active
          ? 'bg-[var(--brand)] text-[var(--brand-ink)]'
          : danger
          ? 'text-[var(--danger,#c0392b)] hover:bg-[color-mix(in_srgb,var(--danger,#c0392b)_12%,transparent)]'
            : 'text-[var(--ink-2)] hover:bg-[var(--surface)] hover:text-[var(--ink)]',
        disabled && 'cursor-not-allowed opacity-35 hover:bg-transparent',
      )}
    >
      {children}
    </button>
  );
}

/** ไอคอนจัดแนวตั้งในเซลล์ — กรอบเซลล์ + บรรทัดข้อความที่ตำแหน่งบน/กลาง/ล่าง */
function VAlignIcon({ mode }: { mode: CellVerticalAlign }) {
  const y = mode === 'top' ? 4 : mode === 'middle' ? 7.2 : 10.4;
  return (
    <svg width="13" height="13" viewBox="0 0 16 16" aria-hidden="true">
      <rect x="1.5" y="1.5" width="13" height="13" rx="1.5" fill="none"
            stroke="currentColor" strokeWidth="1.2" opacity=".45" />
      <rect x="4" y={y} width="8" height="1.5" rx=".75" fill="currentColor" />
      <rect x="4" y={y + 2.2} width="5" height="1.5" rx=".75" fill="currentColor" />
    </svg>
  );
}

const VALIGN_LABELS: Record<CellVerticalAlign, string> = {
  top: 'ชิดบน',
  middle: 'กึ่งกลางแนวตั้ง',
  bottom: 'ชิดล่าง',
};

function Sep() {
  return <span className="mx-0.5 h-4 w-px shrink-0 bg-[var(--line)]" aria-hidden="true" />;
}

export function TableToolbar({ editor }: { editor: Editor }) {
  const can = editor.can();
  return (
    <div className="flex flex-wrap items-center gap-0.5 border-b border-[var(--line)] bg-[var(--sunken)] px-2 py-1">
      <span className="mr-1 text-xs font-semibold text-[var(--ink-2)]">ตาราง</span>

      <ToolButton title="แทรกแถวเหนือบรรทัดนี้" disabled={!can.addRowBefore()}
                  onClick={() => editor.chain().focus().addRowBefore().run()}>+ แถวบน</ToolButton>
      <ToolButton title="แทรกแถวใต้บรรทัดนี้" disabled={!can.addRowAfter()}
                  onClick={() => editor.chain().focus().addRowAfter().run()}>+ แถวล่าง</ToolButton>
      <ToolButton title="ลบแถวนี้" disabled={!can.deleteRow()}
                  onClick={() => editor.chain().focus().deleteRow().run()}>− แถว</ToolButton>
      <Sep />
      <ToolButton title="แทรกคอลัมน์ทางซ้าย" disabled={!can.addColumnBefore()}
                  onClick={() => editor.chain().focus().addColumnBefore().run()}>+ คอลัมน์ซ้าย</ToolButton>
      <ToolButton title="แทรกคอลัมน์ทางขวา" disabled={!can.addColumnAfter()}
                  onClick={() => editor.chain().focus().addColumnAfter().run()}>+ คอลัมน์ขวา</ToolButton>
      <ToolButton title="ลบคอลัมน์นี้" disabled={!can.deleteColumn()}
                  onClick={() => editor.chain().focus().deleteColumn().run()}>− คอลัมน์</ToolButton>
      <Sep />
      <ToolButton title="รวมเซลล์ที่เลือกเป็นเซลล์เดียว" disabled={!can.mergeCells()}
                  onClick={() => editor.chain().focus().mergeCells().run()}>รวมเซลล์</ToolButton>
      <ToolButton title="แยกเซลล์ที่ถูกรวมไว้" disabled={!can.splitCell()}
                  onClick={() => editor.chain().focus().splitCell().run()}>แยกเซลล์</ToolButton>
      <Sep />
      {CELL_VERTICAL_ALIGNS.map((mode) => (
        <ToolButton
          key={mode}
          title={`จัดข้อความในเซลล์${VALIGN_LABELS[mode]}`}
          // ชิดบนเป็นค่าเริ่มต้น เซลล์ที่ยังไม่เคยตั้งค่าจึงเก็บเป็น null — ให้ปุ่มชิดบนติดไว้
          active={editor.isActive('tableCell', { verticalAlign: mode })
            || editor.isActive('tableHeader', { verticalAlign: mode })
            || (mode === 'top'
              && (editor.isActive('tableCell', { verticalAlign: null })
                || editor.isActive('tableHeader', { verticalAlign: null })))}
          onClick={() => editor.chain().focus().setCellAttribute('verticalAlign', mode === 'top' ? null : mode).run()}
        >
          <VAlignIcon mode={mode} />
        </ToolButton>
      ))}
      <Sep />
      <ToolButton title="สลับว่าแถวบนสุดเป็นหัวตารางหรือไม่" disabled={!can.toggleHeaderRow()}
                  onClick={() => editor.chain().focus().toggleHeaderRow().run()}>แถวหัวตาราง</ToolButton>
      <ToolButton title="สลับว่าคอลัมน์ซ้ายสุดเป็นหัวตารางหรือไม่" disabled={!can.toggleHeaderColumn()}
                  onClick={() => editor.chain().focus().toggleHeaderColumn().run()}>คอลัมน์หัวตาราง</ToolButton>

      <span className="ml-auto" />
      <ToolButton title="ลบทั้งตาราง" danger disabled={!can.deleteTable()}
                  onClick={() => editor.chain().focus().deleteTable().run()}>
        <IconTrash size={12} /> ลบตาราง
      </ToolButton>
    </div>
  );
}
